/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Loader from '@amzn/meridian/loader';
import Pagination from '@amzn/meridian/pagination';
import Row from '@amzn/meridian/row';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';
import Icon from '@amzn/meridian/icon';
import chatTokens from '@amzn/meridian-tokens/base/icon/chat';
import Box from '@amzn/meridian/box';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Tooltip from '@amzn/meridian/tooltip';
import DataSource from '../../../api';
import {
  CoachingDetailsInput,
  CoachingFormData,
  CoachingFormInputData,
  CoachingSessionInput,
  ContactInput,
  defaultCoachingFormValues,
  defaultCoachingSessionValues,
  DeliveredStatusObject,
  enumEmpty,
  StatusOptionsObject,
} from '../../../api/CoachingForm/schema';
import { StateContext } from '../../../GlobalStateStore';
import DataState from '../../../Types/DataState';
import { getCurrentWeek } from '../../../Util';
import { renderUrl, urls } from '../../../config/Url';
import useCustomTracking from '../../../Util/useCustomTracking';
import { convertDetailsToDisplay } from '../../CoachingForm/CoachingDetails/Container';
import { getAllTopics } from '../../CoachingForm/CoachingDetails/Data';
import { convertSessionToDisplay } from '../../CoachingForm/CoachingSession/FormPanel';
import { convertReviewToDisplay } from '../../CoachingForm/ContactReview/Container';
import { getLabelFromValue } from '../../CoachingForm/FieldComponents/Helpers';
import FieldArrayDisplay from '../../CoachingForm/LayoutComponents/FieldArrayDisplay';
import { getAllReasonOptions } from '../../CoachingForm/NoCoaching/Data';
import { convertReasonToDisplay } from '../../CoachingForm/NoCoaching/Form';
import trimCoachingData from '../../CoachingForm/trimCoachingData';
import { getLocaleString } from '../../../translations/index';
import { EmptyCoachingFormData } from '../../../Types/CoachingForm';
import Page from '../../../Types/Page';
import TimeoutToast from '../../CoachingForm/AutoSave/TimeoutToast';
import { Feature, isFeatureEnabled } from '../../../config/FeaturesConfig';
import { getRequesterRole } from '../../OMCoaching/helper';
import {
  ExpandableTableProps,
  CoachingHistoryProps,
  TableRowData,
  sortDetail,
  parseNotes,
  parseHistory,
  convertLegacyNoteToDispaly,
  filterNoteForDashboard,
  CoachingHistoryColumn,
  showColumn,
  IsExpandableRow,
  getExpandableContentColumnSpan,
} from './CoachingHistoryHelpers';
import CoachingCommentModal from '../../CaseManagement/CoachingCommentModal';
import { convertGoalsToDisplay } from '../../CoachingForm/AttachedGoals/Container';
import { defaultAWSYear } from '../../../api/CoachingForm/schema';
import CoachingEmptyState from '../../../Assets/coachingEmptyState.png';

const getCellEndPadding = (isCoachingNotePage: boolean): any => {
  let paddings: React.ReactNode[] = [];
  const paddingCount =
    getExpandableContentColumnSpan(isCoachingNotePage).padding;
  for (let i = 0; i < paddingCount; i++) {
    paddings.push(<TableCell />);
  }
  return paddings;
};

const UncontrolledExpandableRow: React.FC<ExpandableTableProps> = ({
  rowCount,
  rowComponents,
  currentPage,
  currentSortColumn,
  currentSortDirection,
  isExpandable,
  children,
  pageName,
  noteId,
  coachingId,
}: ExpandableTableProps): React.ReactElement => {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [column, setColumn] = useState('lastUpdated');
  const [direction, setDirection] = useState('descending');

  const onClick = useCallback(() => setOpen(!open), [open]);

  const isPageChanged: boolean = currentPage !== page;
  const isDirectionChanged: boolean = currentSortDirection !== direction;
  const isColumnChanged: boolean = currentSortColumn !== column;

  const handleOpen = (
    opened: boolean,
    pageChanged: boolean,
    directionChanged: boolean,
    columnChanged: boolean
  ): boolean => {
    if (pageChanged && opened) {
      setOpen(false);
      setPage(currentPage);
      return !opened;
    }

    if (directionChanged && opened) {
      setOpen(false);
      setDirection(currentSortDirection);
      return !opened;
    }

    if (columnChanged && opened) {
      setOpen(false);
      setColumn(currentSortColumn);
      return !opened;
    }

    return opened;
  };

  return isExpandable ? (
    <TableRow
      data-csa-c-type='widget'
      data-csa-c-slot-id='coaching-history-coaching-note-expander-slot'
      data-csa-c-content-id='coaching-history-coaching-note-expander'
      data-csa-c-tag1={pageName}
      data-csa-c-tag2='Coaching History'
      data-csa-c-noteid={noteId}
      data-cy={`coaching-history-coaching-note-expander-${coachingId}`}
      open={handleOpen(
        open,
        isPageChanged,
        isDirectionChanged,
        isColumnChanged
      )}
      onClick={onClick}
      rowComponents={rowComponents}
      highlightOnHover={false}
      backgroundColor={rowCount % 2 !== 0 ? 'secondary' : 'primary'}
      key={`${currentPage}_${rowCount}`}
      data-testid='coaching-history'
    >
      {children}
    </TableRow>
  ) : (
    <TableRow
      highlightOnHover={false}
      backgroundColor={rowCount % 2 !== 0 ? 'secondary' : 'primary'}
      key={`${currentPage}_${rowCount}`}
      data-testid='coaching-history'
    >
      {children}
    </TableRow>
  );
};

const CoachingHistory: React.FC<CoachingHistoryProps> = ({
  coachingHistory,
  isTeam,
  legacyNotes,
  profile,
  profileState,
  page,
  refreshCoachingHistory,
}: CoachingHistoryProps): React.ReactElement => {
  const intl = useIntl();
  const { userId } = useParams() as { userId: string };
  const navigate = useNavigate();
  const caseManagementEnabled = isFeatureEnabled(Feature.CASE_MANAGEMENT);
  const goalSettingEnabled = isFeatureEnabled(Feature.GOAL_SETTING);

  const apiNaws = DataSource.getDataFeed('NAWS');
  const [createState, setCreateState] = useState(DataState.LOADING);
  const [, setMarkCompleteState] = useState(DataState.LOADING);
  const [openCloseSessionModal, setOpenCloseSessionModal] = useState(false);
  const [closingSession, setClosingSession] = useState(DataState.INITIALIZED);
  const [reviewCoachingMutationState, setReviewCoachingMutationState] =
    useState(DataState.INITIALIZED);

  const [closingCoachingFormInfo, setClosingCoachingFormInfo] = useState(
    EmptyCoachingFormData
  );
  const [confirmToClose, setConfirmToClose] = useState(false);
  const [openCoachingComment, setOpenCoachingComment] = useState(false);

  const [isClosedSession, setIsClosedSession] = useState(false);
  const [selectedNoteID, setSelectedNoteID] = useState('');
  const [selectedCoachingID, setSelectedCoachingID] = useState('');
  const [selectedCoach, setSelectedCoach] = useState('');
  const [isSelectedCoachingIdEmpty, setIsSelectedCoachingIdEmpty] =
    useState(false);

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { cscEntityPrefix, isTm, isOm } = inspireOptions.data;
  const handleReplace = (
    value: CoachingDetailsInput,
    note: TableRowData
  ): void => {
    let filterDetails;
    if ('coachingDetails' in note.data) {
      filterDetails = note.data.coachingDetails.filter(
        detail =>
          detail.relatedMetric === value.relatedMetric &&
          detail.behaviors === value.behaviors &&
          detail.assignedAction === value.assignedAction &&
          detail.coachingTopic === value.coachingTopic &&
          detail.relatedContacts === value.relatedContacts &&
          detail.tenets === value.tenets
      );
      if (filterDetails[0] !== undefined) {
        filterDetails[0].status = StatusOptionsObject.CLOSED;
      }
    }
    const newValues =
      'coachingDetails' in note.data
        ? JSON.parse(JSON.stringify(note.data))
        : null;
    const trimmedValue =
      'coachingDetails' in note.data && newValues !== null
        ? trimCoachingData(newValues)
        : null;
    if (trimmedValue !== null)
      apiNaws.updateCoachingNote(trimmedValue, setMarkCompleteState);
  };

  let filteredCoaching: TableRowData[] = [];
  let parsedHistory: TableRowData[] = [];

  if (coachingHistory !== undefined) {
    sortDetail(coachingHistory, intl);
    parsedHistory = parseHistory(coachingHistory, intl);
    filteredCoaching = isTeam
      ? parsedHistory
      : filterNoteForDashboard(parsedHistory);
  }

  const [sortColumn, setSortColumn] = useState<keyof TableRowData & string>(
    caseManagementEnabled ? 'createdAt' : 'lastUpdated'
  );
  const [sortDirection, setSortDirection] = useState('descending');

  const compareTwoArray = (
    arr1: string[],
    arr2: string[],
    arr1Len: number,
    arr2Len: number
  ): number => {
    let arr1Index = 0;
    let arr2Index = 0;

    while (arr1Index < arr1Len && arr2Index < arr2Len) {
      if (arr1[arr1Index] === arr2[arr2Index]) {
        arr1Index += 1;
        arr2Index += 1;
      } else {
        if (arr1[arr1Index] > arr2[arr2Index])
          return sortDirection === 'ascending' ? 1 : -1;
        if (arr1[arr1Index] < arr2[arr2Index])
          return sortDirection === 'ascending' ? -1 : 1;
      }
    }

    if (arr1Index === arr1Len && arr2Index === arr2Len) return 0;

    return arr1Index === arr1Len && sortDirection === 'ascending' ? -1 : 1;
  };

  const customSort = (noteA: TableRowData, noteB: TableRowData): number => {
    if (sortColumn === 'coaching') {
      return compareTwoArray(
        noteA.coaching,
        noteB.coaching,
        noteA.coaching.length,
        noteB.coaching.length
      );
    }

    let valueA;
    let valueB;
    if (sortColumn === 'goals') {
      valueA = noteA.goals.length;
      valueB = noteB.goals.length;
    } else {
      valueA = noteA[sortColumn];
      valueB = noteB[sortColumn];
    }

    if (valueA < valueB) {
      return sortDirection === 'ascending' ? -1 : 1;
    } else if (valueA > valueB) {
      return sortDirection === 'ascending' ? 1 : -1;
    } else {
      return 0;
    }
  };

  if (filteredCoaching !== undefined) {
    filteredCoaching.sort((noteA: TableRowData, noteB: TableRowData) =>
      customSort(noteA, noteB)
    );
  }

  let parsedNote: TableRowData[] = [];
  let newCoachings: TableRowData[] = [];

  if (legacyNotes !== undefined) {
    parsedNote = parseNotes(legacyNotes, intl);
  }

  if (filteredCoaching !== undefined) {
    newCoachings =
      parsedNote !== undefined
        ? filteredCoaching.concat(parsedNote)
        : filteredCoaching;
    newCoachings.sort((noteA: TableRowData, noteB: TableRowData) =>
      customSort(noteA, noteB)
    );
  }

  const legacyNotesLen = legacyNotes ? legacyNotes.length : 0;
  const filteredCoachingLen = filteredCoaching ? filteredCoaching.length : 0;
  const visibleRecordsLen = legacyNotesLen + filteredCoachingLen;

  const itemsPerPage = 10;
  const numberOfPages = Math.ceil(visibleRecordsLen / itemsPerPage);

  const [currentPage, setCurrentPage] = useState(1);
  const [generatingForm, setGeneratingForm] = useState(false);
  const firstVisibleIndex = (currentPage - 1) * itemsPerPage;
  const lastVisibleIndex = firstVisibleIndex + itemsPerPage;

  // eslint-disable-next-line no-shadow
  const onSort = useCallback(
    ({
      sortColumn,
      sortDirection,
    }: {
      sortColumn: keyof TableRowData & string;
      sortDirection: string;
    }) => {
      setSortDirection(sortDirection);
      setSortColumn(sortColumn);
    },
    []
  );

  const [TableSortTrackingTag, tableSortClick] = useCustomTracking(
    'coaching-history-table-sort',
    'coaching-history-table-sort',
    {
      'data-csa-c-type': 'table',
      'data-csa-c-sort-column': sortColumn || '',
      'data-csa-c-sort-order': sortDirection || '',
      'data-csa-c-tag1': page,
      'data-csa-c-tag2': 'Coaching History',
    }
  );
  useEffect(() => {
    tableSortClick();
  }, [sortColumn, sortDirection]);

  const queryRecipient = apiNaws.prepareQuery(
    getCurrentWeek(),
    userId,
    cscEntityPrefix
  );

  const isValidDeliveredDate = (deliveredDate?: string): boolean => {
    if (!deliveredDate) return false;

    const deliveredMoment = moment.utc(deliveredDate);

    return (
      deliveredMoment.isValid() && deliveredMoment.year() !== defaultAWSYear
    );
  };

  useEffect(() => {
    if (createState === DataState.LOADED) {
      navigate(renderUrl(`${urls.coachingForm}/${userId}`));
    }
  }, [createState]);

  const handleCloseSession = async (
    selectedCoachingFormInfo: CoachingFormData
  ): Promise<void> => {
    setOpenCloseSessionModal(false);

    if (selectedCoachingFormInfo.id) {
      const newValues: CoachingFormData = {
        ...selectedCoachingFormInfo,
        isDelivered: DeliveredStatusObject.CLOSED,
      };
      const trimmedValues: CoachingFormInputData = trimCoachingData(newValues);
      await apiNaws.updateCoachingNote(
        {
          ...trimmedValues,
          deliveredAt: selectedCoachingFormInfo.deliveredAt,
          goalIDs:
            selectedCoachingFormInfo.goals?.map(goal => goal.goalID) ?? [],
        },
        setClosingSession,
        getLocaleString(),
        false
      );

      refreshCoachingHistory();
    }
  };

  const handleReviewSession = async (
    selectedCoachingFormInfo: CoachingFormData
  ): Promise<void> => {
    if (selectedCoachingFormInfo.id) {
      const newValues: CoachingFormData = {
        ...selectedCoachingFormInfo,
        isDelivered: DeliveredStatusObject.OPEN_REVIEWED,
      };

      const trimmedValues: CoachingFormInputData = trimCoachingData(newValues);
      await apiNaws.updateCoachingNote(
        {
          ...trimmedValues,
          deliveredAt: selectedCoachingFormInfo.deliveredAt,
          goalIDs:
            selectedCoachingFormInfo.goals?.map(goal => goal.goalID) ?? [],
        },
        setReviewCoachingMutationState,
        getLocaleString(),
        false
      );

      refreshCoachingHistory();
    }
  };

  useEffect(() => {
    if (confirmToClose) {
      handleCloseSession(closingCoachingFormInfo);
    }
  }, [confirmToClose]);

  if (profileState === DataState.LOADING) {
    return (
      <div style={{ textAlign: 'center' }}>
        <Loader type='circular' size='large' />
      </div>
    );
  }

  if (profileState === DataState.ERROR) {
    return (
      <Alert type='error'>
        <FormattedMessage
          id='cs_perfportal_error_reason_1'
          defaultMessage='Your credentials may be incorrect or inadequate.'
        />
      </Alert>
    );
  }

  const handleFormCreation = (): void => {
    setGeneratingForm(true);
    // Empty Coaching Form Input
    const initialValues: CoachingFormInputData = {
      id: '',
      recipient: {
        userID: `${userId ? queryRecipient.userID : ''}`,
        userName: `${profile.user.userName}`,
      },
      coach: {
        userID: '',
        userName: '',
      },
      isDelivered: DeliveredStatusObject.DRAFT,
      contactReview: [],
      coachingDetails: [],
      coachingSession: {
        managerNotes: '',
        coachingTactic: enumEmpty,
        coachingMethod: enumEmpty,
        duration: enumEmpty,
        preparationTime: enumEmpty,
        coachingSummaryNotes: '',
      },
      otherReason: '',
      reason: enumEmpty,
    };
    apiNaws.updateCoachingNote(initialValues, setCreateState);
  };

  return visibleRecordsLen === 0 ? (
    <Column width='100%' alignmentHorizontal='center' spacing='300'>
      <div>
        <img src={CoachingEmptyState} alt='no coachings' />
      </div>
      <Row alignmentHorizontal='center'>
        <Text type='b300'>
          <FormattedMessage
            id='cs_perfportal_coaching_noCoachings'
            defaultMessage="You don't have any coachings to view yet"
          />
        </Text>
      </Row>
      {isTeam ? (
        <Row alignmentHorizontal='center' spacingInset='400'>
          <Text alignment='right'>
            <Button
              data-csa-c-type='button'
              data-csa-c-slot-id='dashboard-tables-coaching-history-button-slot'
              data-csa-c-content-id='dashboard-tables-coaching-history-start-coaching-button'
              data-csa-c-tag1={page}
              data-csa-c-tag2='Coaching History'
              data-csa-c-profile-id={`${userId}`}
              disabled={generatingForm}
              href={renderUrl(`${urls.coachingForm}/${userId}`)}
              onClick={(): void => handleFormCreation()}
            >
              {generatingForm ? (
                <Loader type='circular' size='small' />
              ) : (
                <FormattedMessage
                  id='cs_perfportal_coaching_start_coaching'
                  defaultMessage='Start coaching'
                />
              )}
            </Button>
          </Text>
        </Row>
      ) : null}
    </Column>
  ) : (
    <Column width='100%' alignmentHorizontal='center' spacing='300'>
      {isTeam && (
        <>
          {/* Toast of successfully/failed to close session */}
          <TimeoutToast
            show={
              closingSession === DataState.SUCCESS ||
              closingSession === DataState.LOADED
            }
            message={intl.formatMessage({
              id: 'cs_perfportal_coaching_closed_successfully',
              defaultMessage: 'Coaching closed successfully',
            })}
            duration={3000}
            type='success'
            onClose={() => {}}
          />
          <TimeoutToast
            show={closingSession === DataState.ERROR}
            message={intl.formatMessage({
              id: 'cs_perfportal_coaching_closed_failed',
              defaultMessage: 'Unable to close coaching',
            })}
            duration={3000}
            type='error'
            onClose={() => {}}
          />
          {/* Modal on confirming to close session */}
          <Modal
            title={intl.formatMessage(
              {
                id: 'cs_perfportal_coaching_closed_coaching_id',
                defaultMessage: `Close coaching ID: ${selectedCoachingID}`,
              },
              { coachingId: selectedCoachingID }
            )}
            open={openCloseSessionModal}
            onClose={() => setOpenCloseSessionModal(false)}
            scrollContainer='viewport'
            closeLabel={intl.formatMessage(
              {
                id: 'cs_perfportal_coaching_closed_coaching_id',
                defaultMessage: `Close coaching ID: ${selectedCoachingID}`,
              },
              { coachingId: selectedCoachingID }
            )}
          >
            <Text>
              <FormattedMessage
                id='cs_perfportal_coaching_closing_confirmation_msg'
                defaultMessage='Are you sure that you want to close the coaching?'
              />
            </Text>
            {closingSession === DataState.LOADING && (
              <Loader type='linear' size='small' />
            )}
            <ModalFooter>
              <Row alignmentHorizontal='end' widths='fit'>
                <Button
                  data-csa-c-type='button'
                  data-csa-c-slot-id='coaching-session-close-coaching-confirm-no-button-slot'
                  data-csa-c-content-id={`coaching-session-close-coaching-confirm-no-button-for-${selectedCoachingID}`}
                  data-csa-c-noteid={selectedNoteID}
                  data-csa-c-tag1={page}
                  data-csa-c-tag2='Coaching History'
                  data-csa-c-tag3='Case Management'
                  data-csa-c-coachingID={selectedCoachingID}
                  data-csa-c-is-tm={isTm}
                  type='secondary'
                  size='small'
                  onClick={() => setOpenCloseSessionModal(false)}
                >
                  <FormattedMessage id='cs_perfportal_no' defaultMessage='No' />
                </Button>
                <Button
                  data-csa-c-type='button'
                  data-csa-c-slot-id='coaching-session-close-coaching-confirm-yes-button-slot'
                  data-csa-c-content-id={`coaching-session-close-coaching-confirm-yes-button-for-${selectedCoachingID}`}
                  data-csa-c-noteid={selectedNoteID}
                  data-csa-c-tag1={page}
                  data-csa-c-tag2='Coaching History'
                  data-csa-c-tag3='Case Management'
                  data-csa-c-coachingID={selectedCoachingID}
                  data-csa-c-is-tm={isTm}
                  data-cy='confirm-close-coaching-note'
                  type='primary'
                  size='small'
                  onClick={() => setConfirmToClose(true)}
                >
                  <FormattedMessage
                    id='cs_perfportal_yes'
                    defaultMessage='Yes'
                  />
                </Button>
              </Row>
            </ModalFooter>
          </Modal>
        </>
      )}
      <Table
        headerRows={1}
        showDividers
        sortColumn={sortColumn}
        sortDirection={
          sortDirection === 'ascending' ? 'ascending' : 'descending'
        }
        onSort={onSort}
        rowComponents={[UncontrolledExpandableRow, TableRow]}
        layout='fixed'
      >
        <TableSortTrackingTag />
        {/* table header line */}
        <TableRow>
          {showColumn(CoachingHistoryColumn.LAST_UPDATE) && (
            <TableCell width='20.6%' sortColumn='lastUpdated'>
              <FormattedMessage
                id='cs_perfportal_coaching_lastUpdate'
                defaultMessage='Last update'
              />
            </TableCell>
          )}
          {showColumn(CoachingHistoryColumn.COACHING_ID) && (
            <TableCell width='30%'>
              <FormattedMessage
                id='cs_perfportal_coaching_id'
                defaultMessage='Coaching ID'
                description='Coaching History Coaching ID'
              />
            </TableCell>
          )}
          {showColumn(CoachingHistoryColumn.COACHING_TOPIC) && (
            <TableCell width='40%' sortColumn='coaching'>
              <FormattedMessage
                id='cs_perfportal_coaching_history_coaching_topics'
                defaultMessage='Coaching topics'
                description='Coaching History Coaching Topics'
              />
            </TableCell>
          )}
          {showColumn(CoachingHistoryColumn.OPEN_DATE) && (
            <TableCell width='20%' sortColumn='createdAt'>
              <FormattedMessage
                id='cs_perfportal_coaching_history_open_date'
                defaultMessage='Open date'
                description='Coaching History Open Date'
              />
            </TableCell>
          )}
          {showColumn(CoachingHistoryColumn.DELIVERED_DATE) && (
            <TableCell width='20%' sortColumn='lastUpdated'>
              <FormattedMessage
                id='cs_perfportal_coaching_history_delivered_date'
                defaultMessage='Delivered date'
                description='Coaching History Delivered Date'
              />
            </TableCell>
          )}
          {showColumn(CoachingHistoryColumn.COACH) && (
            <TableCell width='20%' sortColumn='lastUpdatedBy'>
              <FormattedMessage
                id='cs_perfportal_coaching_history_coach'
                defaultMessage='Coach'
              />
            </TableCell>
          )}
          {showColumn(CoachingHistoryColumn.COMMENTS) && (
            <TableCell width='20%'>
              <FormattedMessage
                id='cs_perfportal_coaching_history_comments'
                defaultMessage='Comments'
              />
            </TableCell>
          )}
          {showColumn(CoachingHistoryColumn.GOAL) && (
            <TableCell width='20%' sortColumn='goals' data-cy='goals-column'>
              <FormattedMessage id='cs_perfportal_goal' defaultMessage='Goal' />
            </TableCell>
          )}
          {showColumn(CoachingHistoryColumn.STATUS) && (
            <TableCell width='20%' sortColumn='status'>
              <FormattedMessage
                id='cs_perfportal_coaching_history_status'
                defaultMessage='Status'
                description='Coaching History Status'
              />
            </TableCell>
          )}
          {showColumn(CoachingHistoryColumn.BUTTON_PLACEHOLDER, isTeam) && (
            <TableCell width='9.4%' />
          )}
        </TableRow>

        {newCoachings !== undefined &&
          newCoachings
            .slice(firstVisibleIndex, lastVisibleIndex)
            .map((coachingNote: TableRowData, index: number) => {
              const { lastUpdated, coach, isLegacy } = coachingNote;

              let isCoached = false;
              let isNoSession = false;
              let isDraft = false;
              let isOpenDelivered = false;
              let isOpenReviewed = false;
              let isClosed = false;

              const selectCoaching = () => {
                setIsClosedSession(isClosed);
                setSelectedNoteID(noteId);
                setSelectedCoachingID(
                  `${
                    !coachingId
                      ? intl.formatMessage({
                          id: 'cs_perfportal_coaching_not_available',
                          defaultMessage: `Not available`,
                        })
                      : coachingId
                  }`
                );
                setSelectedCoach(coach);
                setIsSelectedCoachingIdEmpty(!coachingId);
              };

              const noteUniqueID =
                'coachingDetails' in coachingNote.data
                  ? coachingNote.data.id
                  : lastUpdated;

              if (!isLegacy) {
                if ('isDelivered' in coachingNote.data) {
                  isCoached =
                    coachingNote.data.isDelivered ===
                    DeliveredStatusObject.DELIVERED;
                  isNoSession =
                    coachingNote.data.isDelivered ===
                    DeliveredStatusObject.NOCOACH;
                  isOpenDelivered =
                    coachingNote.data.isDelivered ===
                    DeliveredStatusObject.OPEN_DELIVERED;
                  isOpenReviewed =
                    coachingNote.data.isDelivered ===
                    DeliveredStatusObject.OPEN_REVIEWED;
                  isClosed =
                    coachingNote.data.isDelivered ===
                    DeliveredStatusObject.CLOSED;
                }

                isDraft =
                  isTeam &&
                  !isCoached &&
                  !isOpenDelivered &&
                  !isOpenReviewed &&
                  !isClosed &&
                  !isNoSession;
              }

              let contactReview: ContactInput[] = [];
              let coachingDetails: CoachingDetailsInput[] = [];
              let coachingSession: CoachingSessionInput =
                defaultCoachingSessionValues;
              let noCoachReason: CoachingFormInputData =
                defaultCoachingFormValues;

              if (
                !isLegacy &&
                (isCoached ||
                  isOpenDelivered ||
                  isOpenReviewed ||
                  isClosed ||
                  isDraft)
              ) {
                if ('contactReview' in coachingNote.data)
                  contactReview = [...coachingNote.data.contactReview];

                if ('coachingDetails' in coachingNote.data) {
                  coachingDetails = [...coachingNote.data.coachingDetails];
                }

                if ('coachingSession' in coachingNote.data)
                  coachingSession = {
                    ...coachingSession,
                    ...coachingNote.data.coachingSession,
                  };
              }

              if (!isLegacy && isNoSession) {
                noCoachReason = { ...noCoachReason, ...coachingNote.data };
              }

              let legacyContent = '';

              if (isLegacy && 'content' in coachingNote.data) {
                legacyContent = coachingNote.data.content;
                if (legacyContent.length > 100)
                  // eslint-disable-next-line no-param-reassign
                  coachingNote.isExpandable = true;
              }

              let coachingId =
                'coachingId' in coachingNote.data
                  ? coachingNote.data.coachingId
                  : '';

              const isHighlight: boolean = index % 2 !== 0;

              const isCoachingSessionEmpty: boolean =
                coachingSession.coachingTactic === enumEmpty &&
                coachingSession.duration === enumEmpty &&
                coachingSession.coachingMethod === enumEmpty &&
                coachingSession.preparationTime === enumEmpty &&
                coachingSession.managerNotes.length === 0 &&
                coachingSession.coachingSummaryNotes.length === 0;

              const isContactEmpty = contactReview.length === 0;
              const isDetailEmpty = coachingDetails.length === 0;
              const isNoSessionEmpty = noCoachReason.reason === enumEmpty;

              // eslint-disable-next-line no-param-reassign
              coachingNote.isExpandable =
                isCoachingSessionEmpty &&
                isContactEmpty &&
                isDetailEmpty &&
                isNoSessionEmpty
                  ? false
                  : coachingNote.isExpandable;

              let noteId = '';
              if (coachingNote.data.id) {
                noteId = coachingNote.data.id;
              }
              return (
                <UncontrolledExpandableRow
                  rowComponents={[UncontrolledExpandableRow, TableRow]}
                  key={`${currentPage}_${index}_${noteUniqueID}`}
                  rowCount={index}
                  currentPage={currentPage}
                  currentSortColumn={sortColumn}
                  currentSortDirection={sortDirection}
                  isExpandable={coachingNote.isExpandable}
                  pageName={page}
                  noteId={noteId}
                  coachingId={coachingId}
                >
                  {/* table data lines */}
                  {showColumn(CoachingHistoryColumn.COACHING_ID) && (
                    <TableCell data-testid='coaching-id'>{`${
                      !coachingId
                        ? intl.formatMessage({
                            id: 'cs_perfportal_coaching_not_available',
                            defaultMessage: `Not available`,
                          })
                        : coachingId
                    }`}</TableCell>
                  )}
                  {showColumn(CoachingHistoryColumn.LAST_UPDATE) && (
                    <TableCell>
                      {moment(lastUpdated).utc().format('YYYY-MM-DD')}
                    </TableCell>
                  )}

                  {showColumn(CoachingHistoryColumn.COACHING_TOPIC) && (
                    <>
                      {isNoSession && (
                        <TableCell>
                          {getLabelFromValue(
                            noCoachReason.reason,
                            getAllReasonOptions(intl)
                          )}
                        </TableCell>
                      )}

                      {(isCoached ||
                        isOpenDelivered ||
                        isOpenReviewed ||
                        isClosed) && (
                        <TableCell>
                          {coachingDetails.map(
                            (
                              detail: CoachingDetailsInput,
                              detailIndex: number
                            ) =>
                              detailIndex !== coachingDetails.length - 1
                                ? `${getLabelFromValue(
                                    detail.coachingTopic,
                                    getAllTopics(intl)
                                  )}, `
                                : `${getLabelFromValue(
                                    detail.coachingTopic,
                                    getAllTopics(intl)
                                  )}`
                          )}
                        </TableCell>
                      )}

                      {isDraft && (
                        <TableCell>
                          {coachingDetails.map(
                            (
                              detail: CoachingDetailsInput,
                              detailIndex: number
                            ) =>
                              detailIndex !== coachingDetails.length - 1
                                ? `${getLabelFromValue(
                                    detail.coachingTopic,
                                    getAllTopics(intl)
                                  )}, `
                                : `${getLabelFromValue(
                                    detail.coachingTopic,
                                    getAllTopics(intl)
                                  )}`
                          )}
                        </TableCell>
                      )}

                      {isLegacy && (
                        <TableCell>
                          <Text truncate>{legacyContent}</Text>
                        </TableCell>
                      )}
                    </>
                  )}
                  {showColumn(CoachingHistoryColumn.OPEN_DATE) && (
                    <TableCell>
                      {moment(coachingNote.data.createdAt)
                        .utc()
                        .format('YYYY-MM-DD')}
                    </TableCell>
                  )}

                  {showColumn(CoachingHistoryColumn.DELIVERED_DATE) && (
                    <TableCell>
                      {isOpenDelivered ||
                      isOpenReviewed ||
                      isCoached ||
                      isClosed
                        ? // For delivered cases, first try to use deliveredDate if it exists and is valid
                          isValidDeliveredDate(coachingNote.data.deliveredAt)
                          ? moment(coachingNote.data.deliveredAt)
                              .utc()
                              .format('YYYY-MM-DD')
                          : // Fallback to lastUpdated if deliveredDate is not valid or doesn't exist (legacy cases)
                            moment(lastUpdated).utc().format('YYYY-MM-DD')
                        : '--'}
                    </TableCell>
                  )}

                  {showColumn(CoachingHistoryColumn.COACH) && (
                    <TableCell>{coach}</TableCell>
                  )}

                  {showColumn(CoachingHistoryColumn.COMMENTS) && (
                    <TableCell>
                      {((isTeam && isOpenDelivered) ||
                        isOpenReviewed ||
                        isClosed) && (
                        <Tooltip
                          position='bottom'
                          title={
                            isClosed
                              ? intl.formatMessage({
                                  id: 'cs_perfportal_coaching_view_comment_icon',
                                  defaultMessage:
                                    'View comments for this coaching session ',
                                })
                              : intl.formatMessage({
                                  id: 'cs_perfportal_coaching_add_or_view_comment_icon',
                                  defaultMessage:
                                    'Add or view comments for this coaching session',
                                })
                          }
                        >
                          <Button
                            data-csa-c-type='button'
                            data-csa-c-slot-id='coaching-session-view-comment-button-slot'
                            data-csa-c-content-id={`coaching-session-view-comment-button-for-${coachingId}`}
                            data-csa-c-noteid={noteId}
                            data-csa-c-tag1={page}
                            data-csa-c-tag2='Coaching History'
                            data-csa-c-tag3='Case Management'
                            data-csa-c-coachingID={coachingId}
                            data-csa-c-is-tm={isTm}
                            data-csa-c-requester-role={getRequesterRole(
                              isOm,
                              isTm
                            )}
                            data-cy='comment-button'
                            propagateClickEvent={false}
                            type='icon'
                            onClick={() => {
                              selectCoaching();
                              setOpenCoachingComment(true);
                            }}
                          >
                            <Icon tokens={chatTokens} />
                          </Button>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                  {showColumn(CoachingHistoryColumn.GOAL) && (
                    <TableCell>
                      {coachingNote.goals.length === 0 ? (
                        <Text data-cy='coaching-goal'>{'--'}</Text>
                      ) : coachingNote.goals.length === 1 ? (
                        <Text data-cy='coaching-goal'>
                          <FormattedMessage
                            id='cs_perfportal_coaching_one_goal_attached'
                            defaultMessage='1 Goal Attached'
                          />
                        </Text>
                      ) : (
                        <Text data-cy='coaching-goal'>
                          <FormattedMessage
                            id='cs_perfportal_coaching_some_goals_attached'
                            defaultMessage='{count} Goals Attached'
                            values={{ count: coachingNote.goals.length }}
                          />
                        </Text>
                      )}
                    </TableCell>
                  )}

                  {showColumn(CoachingHistoryColumn.STATUS) && (
                    <>
                      {isNoSession && (
                        <TableCell>
                          <Text data-cy='coaching-status-no-session'>
                            <FormattedMessage
                              id='cs_perfportal_coaching_status_no_session'
                              defaultMessage='No session'
                            />
                          </Text>
                        </TableCell>
                      )}
                      {(isCoached ||
                        (isLegacy && coachingNote.status === 'Coached')) && (
                        <TableCell>
                          <Text data-cy='coaching-status-coached'>
                            <FormattedMessage
                              id='cs_perfportal_coaching_status_coached'
                              defaultMessage='Coached'
                            />
                          </Text>
                        </TableCell>
                      )}
                      {isOpenDelivered && (
                        <TableCell>
                          <Text data-cy='coaching-status-open-delivered'>
                            <FormattedMessage
                              id='cs_perfportal_coaching_status_open_delivered'
                              defaultMessage='Open(Delivered)'
                            />
                          </Text>
                        </TableCell>
                      )}
                      {isOpenReviewed && (
                        <TableCell>
                          <Text
                            data-cy={`coaching-status-open-reviewed-${coachingId}`}
                          >
                            <FormattedMessage
                              id='cs_perfportal_coaching_status_open_reviewed'
                              defaultMessage='Open(Reviewed)'
                            />
                          </Text>
                        </TableCell>
                      )}
                      {isClosed && (
                        <TableCell>
                          <Text
                            data-cy={`coaching-status-closed-${coachingId}`}
                          >
                            <FormattedMessage
                              id='cs_perfportal_coaching_status_closed'
                              defaultMessage='Closed'
                            />
                          </Text>
                        </TableCell>
                      )}
                      {(isDraft ||
                        (isLegacy &&
                          isTeam &&
                          coachingNote.status === 'Draft')) && (
                        <TableCell>
                          <Text data-cy='coaching-status-draft'>
                            <FormattedMessage
                              id='cs_perfportal_coaching_status_not_coached'
                              defaultMessage='Draft'
                            />
                          </Text>
                        </TableCell>
                      )}
                    </>
                  )}
                  {showColumn(
                    CoachingHistoryColumn.BUTTON_PLACEHOLDER,
                    isTeam,
                    coachingNote
                  ) && <TableCell />}
                  {/* <TableCell /> */}
                  {showColumn(
                    CoachingHistoryColumn.EDIT_BUTTON,
                    isTeam,
                    coachingNote
                  ) && (
                    <TableCell>
                      {/* {isDraft && ( */}
                      <Row alignmentHorizontal='start'>
                        <Link
                          data-cy='edit-coaching-note'
                          data-csa-c-type='link'
                          data-csa-c-slot-id='team-table-coaching-history-edit-slot'
                          data-csa-c-content-id='team-table-coaching-history-edit-link'
                          data-csa-c-profile-id={userId}
                          data-csa-c-tag1={page}
                          data-csa-c-tag2='Coaching History'
                          data-csa-c-noteid={noteId}
                          href={renderUrl(`${urls.coachingForm}/${userId}`)}
                          onClick={navigate}
                          type='secondary'
                        >
                          <FormattedMessage
                            id='cs_perfportal_coaching_edit'
                            defaultMessage='Edit'
                          />
                        </Link>
                      </Row>
                      {/* )} */}
                    </TableCell>
                  )}
                  {showColumn(
                    CoachingHistoryColumn.REVIEW_BUTTON,
                    isTeam,
                    coachingNote
                  ) && (
                    <TableCell>
                      <Row alignmentHorizontal='start'>
                        <Link
                          data-cy='review-coaching-note'
                          data-csa-c-type='button'
                          data-csa-c-slot-id='coaching-session-review-button-slot'
                          data-csa-c-content-id={`coaching-session-review-button-for-${coachingId}`}
                          data-csa-c-noteid={noteId}
                          data-csa-c-tag1={page}
                          data-csa-c-tag2='Coaching History'
                          data-csa-c-tag3='Case Management'
                          data-csa-c-coachingID={coachingId}
                          data-csa-c-is-tm={isTm}
                          type='secondary'
                          disabled={
                            reviewCoachingMutationState === DataState.LOADING
                          }
                          onClick={() => {
                            if ('coach' in coachingNote.data) {
                              handleReviewSession(coachingNote.data);
                            }
                          }}
                        >
                          <FormattedMessage
                            id='cs_perfportal_coaching_review'
                            defaultMessage='Review'
                          />
                        </Link>
                      </Row>
                    </TableCell>
                  )}

                  {IsExpandableRow(isTeam, coachingNote) && (
                    <TableRow
                      backgroundColor={isHighlight ? 'secondary' : 'primary'}
                    >
                      {!caseManagementEnabled && <TableCell />}
                      <TableCell
                        columnSpan={
                          getExpandableContentColumnSpan(isTeam).content
                        }
                      >
                        <Column>
                          {!isNoSession ? (
                            <>
                              {contactReview.length > 0 && (
                                <FieldArrayDisplay
                                  mapKey={`reviewdisplay_${currentPage}_${index}_${noteUniqueID}`}
                                  displayValues={contactReview.map(review =>
                                    convertReviewToDisplay(review, intl)
                                  )}
                                />
                              )}
                              {coachingDetails.length > 0 && (
                                <FieldArrayDisplay
                                  mapKey={`detaildisplay_${currentPage}_${index}_${noteUniqueID}`}
                                  displayValues={coachingDetails.map(
                                    coachingDetail =>
                                      convertDetailsToDisplay(
                                        coachingDetail,
                                        intl,
                                        detail =>
                                          handleReplace(detail, coachingNote),
                                        isTeam
                                      )
                                  )}
                                />
                              )}
                              {!isCoachingSessionEmpty && (
                                <FieldArrayDisplay
                                  mapKey={`sessiondisplay_${currentPage}_${index}_${noteUniqueID}`}
                                  displayValues={convertSessionToDisplay(
                                    coachingSession,
                                    intl,
                                    isTeam
                                  )}
                                />
                              )}
                              {goalSettingEnabled &&
                                coachingNote.goals.length > 0 && (
                                  <FieldArrayDisplay
                                    mapKey={`sessiondisplay_${currentPage}_${index}_${noteUniqueID}`}
                                    displayValues={convertGoalsToDisplay(
                                      coachingNote.goals,
                                      intl
                                    )}
                                  />
                                )}
                            </>
                          ) : (
                            <FieldArrayDisplay
                              mapKey={`nosessiondisplay_${currentPage}_${index}_${noteUniqueID}`}
                              displayValues={convertReasonToDisplay(
                                noCoachReason,
                                intl
                              )}
                            />
                          )}
                          {isLegacy && 'content' in coachingNote.data && (
                            <FieldArrayDisplay
                              mapKey={`legacydisplay_${currentPage}_${index}_${noteUniqueID}`}
                              displayValues={convertLegacyNoteToDispaly(
                                coachingNote.data.content
                              )}
                            />
                          )}
                        </Column>
                        <Row width='100%' minHeight={20}></Row>
                        {caseManagementEnabled && (
                          <>
                            <Row width='100%' widths={['grid-9', 'grid-3']}>
                              {/* add comment button */}
                              {(isOpenDelivered || isOpenReviewed) && (
                                <Box>
                                  <Button
                                    data-csa-c-type='button'
                                    data-csa-c-slot-id='coaching-session-add-comment-button-slot'
                                    data-csa-c-content-id={`coaching-session-add-comment-button-for-${coachingId}`}
                                    data-csa-c-noteid={noteId}
                                    data-csa-c-tag1={page}
                                    data-csa-c-tag2='Coaching History'
                                    data-csa-c-tag3='Case Management'
                                    data-csa-c-coachingID={coachingId}
                                    data-csa-c-is-tm={isTm}
                                    data-csa-c-requester-role={getRequesterRole(
                                      isOm,
                                      isTm
                                    )}
                                    onClick={() => {
                                      selectCoaching();
                                      setOpenCoachingComment(true);
                                    }}
                                    type='link'
                                    size='medium'
                                  >
                                    <FormattedMessage
                                      id='cs_perfportal_coaching_open_comment_modal'
                                      defaultMessage='+ Add comment to coaching session'
                                      description='Link to add comment to coaching session'
                                    />
                                  </Button>
                                </Box>
                              )}
                              {/* close button */}
                              {isTeam && isOpenReviewed && (
                                <Box>
                                  <Column alignmentHorizontal='end'>
                                    <Button
                                      data-csa-c-type='button'
                                      data-csa-c-slot-id='coaching-session-close-coaching-button-slot'
                                      data-csa-c-content-id={`coaching-session-close-coaching-button-for-${coachingId}`}
                                      data-csa-c-noteid={noteId}
                                      data-csa-c-tag1={page}
                                      data-csa-c-tag2='Coaching History'
                                      data-csa-c-tag3='Case Management'
                                      data-csa-c-coachingID={coachingId}
                                      data-csa-c-is-tm={isTm}
                                      data-csa-c-requester-role={getRequesterRole(
                                        isOm,
                                        isTm
                                      )}
                                      data-cy={`close-coaching-note-${coachingId}`}
                                      onClick={() => {
                                        setOpenCloseSessionModal(true);
                                        setSelectedCoachingID(
                                          `${
                                            !coachingId
                                              ? intl.formatMessage({
                                                  id: 'cs_perfportal_coaching_not_available',
                                                  defaultMessage: `Not available`,
                                                })
                                              : coachingId
                                          }`
                                        );
                                        setIsSelectedCoachingIdEmpty(
                                          !coachingId
                                        );
                                        if ('coach' in coachingNote.data) {
                                          const noteCopy = {
                                            ...coachingNote.data,
                                          };
                                          setClosingCoachingFormInfo(noteCopy);
                                        }
                                      }}
                                      type='secondary'
                                      size='medium'
                                    >
                                      <FormattedMessage
                                        id='cs_perfportal_coaching_close_coaching'
                                        defaultMessage='Close coaching'
                                        description='Link to close a coaching session'
                                      />
                                    </Button>
                                  </Column>
                                </Box>
                              )}
                            </Row>
                          </>
                        )}
                      </TableCell>
                      {getCellEndPadding(isTeam)}
                    </TableRow>
                  )}
                </UncontrolledExpandableRow>
              );
            })}
      </Table>

      {/* comment modal */}
      {caseManagementEnabled && (
        <CoachingCommentModal
          isOpen={openCoachingComment}
          setIsOpen={() => setOpenCoachingComment}
          noteID={selectedNoteID}
          coachingID={selectedCoachingID}
          coach={selectedCoach}
          isCoachingIdEmpty={isSelectedCoachingIdEmpty}
          closedCoaching={isClosedSession}
          profile={profile}
          page={page}
        />
      )}
      {visibleRecordsLen > 10 ? (
        <Pagination
          showSkipArrows
          numberOfPages={numberOfPages}
          onChange={setCurrentPage}
          currentPage={currentPage}
        />
      ) : null}
    </Column>
  );
};

export default CoachingHistory;
